import { sha256 } from 'js-sha256'
import type {
  ImageReference,
  PrinterProfile,
  RenderManifest,
  ResolvedImage,
  Symbology,
  Template,
  VariableMeta,
  Warning,
} from '@tsagko/label-contracts'
import { analyzeTemplateRequirements } from './analyze-requirements'
import type { ZplCommand } from './zpl-command'
import { buildRenderPlan, type RenderPlan } from './render-plan'
import { commandsFromPlans } from './commands-from-plans'
import { flattenZones } from './flatten-zones'
import { collectRegionBorders, dedupeBorderSegments } from './region-borders'
import { serializeZpl } from './serialize'
import { PIPELINE_VERSION } from './index'
import { resolvePrintRotation, rotateFlatZones } from './rotate-flat-zones'
import { rotateResolvedImages } from './internal/rotate-mono-bitmap'

export interface GenerateZplResponse {
  zpl: string
  manifest: RenderManifest
  usedFonts: string[]
  usedImages: ImageReference[]
  usedSymbols: Symbology[]
  warnings: Warning[]
}

/**
 * Top-level facade that wires `flattenZones`, `buildRenderPlan`,
 * `commandsFromPlans`, and `serializeZpl` together with the
 * printer-profile-driven setup commands (`^LH`, `^PW`, `^LL`) prepended in
 * front of the body. The render manifest carries SHA-256 of the emitted
 * ZPL, the pipeline version, the generated-at timestamp, the
 * template/profile ids, and the warning list.
 *
 * Determinism note:
 *   The `zpl` string IS deterministic for a given (template, data,
 *   profile, resolvedImages) tuple. The `manifest.generatedAt` field
 *   timestamps the render call and therefore drifts across invocations —
 *   by design, the SHA covers the ZPL output only, not the manifest. Tests
 *   pin determinism on `zpl` and assert `generatedAt` matches a valid ISO
 *   8601 regex rather than an exact value.
 *
 * Manifest metadata:
 *   - `usedFonts`, `usedImages`, `usedSymbols` are sourced from
 *     `analyzeTemplateRequirements(template)` (sub-task 2a.10.16). That
 *     analyzer walks `flattenZones` once and aggregates per-leaf
 *     contributions (font ids, image refs, symbologies, bound field keys);
 *     we map its three `required*` arrays onto the manifest's `used*`
 *     fields verbatim. `requiredFields` is collected too but is not yet
 *     surfaced on the manifest — Phase 2 will reuse it for unmet-binding
 *     diagnostics.
 */
export interface GenerateZplOptions {
  /**
   * Number of copies to print. Clamped to the range 1–999; fractional values
   * are rounded; values ≤ 0 or NaN are treated as 1.
   *
   * When the effective value is 1 (or omitted) no `^PQ` command is emitted,
   * keeping the output byte-identical to previous behaviour.
   */
  copies?: number
}

export function generateZpl(
  template: Template,
  data: Record<string, unknown>,
  printerProfile: PrinterProfile,
  resolvedImages: Map<string, ResolvedImage>,
  variables: Record<string, VariableMeta> = {},
  opts: GenerateZplOptions = {}
): GenerateZplResponse {
  const flat = flattenZones(template)

  // Print-time rotation: a label wider than the print head is rotated 90° so
  // its width runs along the (unbounded) feed direction. resolvePrintRotation
  // returns deg 0 (no rotation) or 90/270. We rotate the flattened zones and
  // image rasters, and swap which dimension feeds ^PW vs ^LL. When deg === 0
  // every value below equals the previous code, so non-rotated goldens stay
  // byte-for-byte unchanged.
  const rotation = resolvePrintRotation(template, printerProfile)
  const rotated = rotation.deg !== 0
  const flatForPlan = rotated
    ? rotateFlatZones(flat, rotation.deg as 90 | 270, template.widthMm, template.heightMm)
    : flat
  const imagesForPlan = rotated
    ? rotateResolvedImages(resolvedImages, rotation.deg)
    : resolvedImages

  // Across-head dimension caps ^PW; the other becomes the feed length (^LL).
  const acrossHeadMm = rotated ? template.heightMm : template.widthMm
  const feedMm = rotated ? template.widthMm : template.heightMm
  const requestedWidthDots = Math.round((acrossHeadMm * printerProfile.dpi) / 25.4)
  const printWidthDots = Math.min(printerProfile.printWidthDots, requestedWidthDots)

  const setupWarnings: Warning[] = []
  if (requestedWidthDots > printerProfile.printWidthDots) {
    setupWarnings.push({
      code: 'PRINT_WIDTH_CLAMPED',
      severity: 'warn',
      message:
        `Label ${rotated ? 'height' : 'width'} ${acrossHeadMm}mm exceeds printer max ` +
        `${printerProfile.usablePrintWidthMm}mm; clamped to printer width`,
    })
  }
  if (rotated) {
    setupWarnings.push({
      code: 'LABEL_ROTATED',
      severity: 'info',
      message:
        `Label rotated 90° ${rotation.direction === 'cw' ? 'clockwise' : 'counter-clockwise'} ` +
        `to fit the ${printerProfile.usablePrintWidthMm}mm print head; ` +
        `width and length are swapped on the printed output.`,
    })
  }

  // ^CI28 switches the printer's field-data interpretation to UTF-8. Without
  // it, a profile's ^FD payloads (which pass accented text through
  // byte-for-byte — see `escapeZplText`) get decoded against the printer's
  // factory single-byte table (commonly CP850-family), corrupting any
  // multi-byte UTF-8 sequence into two mangled glyphs (issue #130, e.g.
  // 'Número' → 'NXmero').
  //
  // Emitted UNCONDITIONALLY, regardless of `printerProfile.encoding`. An
  // earlier version of this comment argued CP850-declared profiles could
  // skip ^CI28 because "CP850 never carries code points above U+00FF" — that
  // premise is false and was corrected after code review (cluster
  // render-129-130-a): `escapeZplText` only warns above U+00FF; it does
  // nothing for the U+0080-U+00FF range (e.g. 'ú' = U+00FA passes through
  // silently for CP850 too), and — independent of that — the wire
  // transmission (`apps/render-backend/src/routes/print.ts`) unconditionally
  // does `Buffer.from(zpl, 'utf8')` before writing to the printer's TCP
  // socket, regardless of the source profile's declared encoding. There is
  // no CP850 (or any other) transcoder anywhere in this codebase, so every
  // profile's ^FD payload is transmitted as UTF-8 bytes on the wire —
  // meaning every profile needs the printer told to interpret it as UTF-8,
  // or accented text corrupts. `encoding: 'CP850'` still has real meaning: it
  // gates `escapeZplText`'s FONT_SUBSTITUTION warning for code points above
  // U+00FF, which those profiles' resident fonts (declared `glyphRanges`)
  // genuinely cannot render — that's a font-glyph-coverage concern, distinct
  // from (and unaffected by) this interpretation-mode preamble.
  //
  // Emitted via the generic `raw` command kind — the lowest-risk mechanism,
  // since it needs no new ZplCommand variant or serializer arm. Must be the
  // very first setup command (right after `^XA`, before ^LH/^PW/^LL) so the
  // interpretation mode is active before anything else is written.
  const setupCommands: ZplCommand[] = [
    { kind: 'raw' as const, value: '^CI28' },
    {
      kind: 'label-home',
      x: printerProfile.media.homeOffsetDots.x,
      y: printerProfile.media.homeOffsetDots.y,
    },
    { kind: 'print-width', dots: printWidthDots },
    {
      kind: 'label-length',
      dots: Math.round((feedMm * printerProfile.dpi) / 25.4),
    },
  ]

  const { plans, warnings: planWarnings } = buildRenderPlan(
    flatForPlan,
    data,
    printerProfile,
    imagesForPlan,
    variables
  )

  // Region-border lines are collected from the flat leaf list (each FlatZone
  // carries the leaf's `borders`), clipped to each cell, de-duped so coincident
  // shared edges draw once, and appended AFTER the content plans so they paint
  // on top of the leaves they bound.
  const borderSegments = dedupeBorderSegments(
    collectRegionBorders(
      flatForPlan.map(z => ({
        xMm: z.xMm,
        yMm: z.yMm,
        widthMm: z.widthMm,
        heightMm: z.heightMm,
        borders: z.borders,
      }))
    )
  )
  const borderPlans: RenderPlan[] = borderSegments.map(
    (s, idx): RenderPlan => ({
      kind: 'border-line',
      id: `border:${idx}`,
      anchorXMm: s.xMm,
      anchorYMm: s.yMm,
      lengthMm: s.lengthMm,
      thicknessMm: s.thicknessMm,
      axis: s.axis,
      style: s.style,
    })
  )

  const { commands: bodyCommands, warnings: emitWarnings } = commandsFromPlans(
    [...plans, ...borderPlans],
    printerProfile
  )
  const buildWarnings = [...setupWarnings, ...planWarnings, ...emitWarnings]

  // Copies: clamp to 1–999, round fractional values; ≤0 / NaN → 1.
  // When the effective count is 1, skip the ^PQ command so the output remains
  // byte-identical to callers that don't pass opts.
  const rawCopies = opts.copies
  const effectiveCopies =
    rawCopies === undefined || !Number.isFinite(rawCopies) || rawCopies <= 0
      ? 1
      : Math.min(999, Math.round(rawCopies))

  const allCommands: ZplCommand[] = [
    ...setupCommands,
    ...bodyCommands,
    ...(effectiveCopies > 1
      ? [{ kind: 'print-quantity' as const, quantity: effectiveCopies }]
      : []),
  ]
  const zpl = serializeZpl(allCommands)

  // SHA-256 of the emitted ZPL string. Manifest schema enforces a 64-char
  // lowercase hex regex. Uses `js-sha256` (a pure-JS implementation) instead
  // of `node:crypto` so the pipeline can run inside Vite's browser bundle
  // — the demo adapter calls `generateZpl()` directly from the browser when
  // pre-computing tier-2 input. `js-sha256` returns the same 64-char
  // lowercase hex digest as Node's `createHash('sha256').digest('hex')`.
  const zplSha256 = sha256(zpl)

  const {
    requiredFonts: usedFonts,
    requiredImages: usedImages,
    requiredSymbols: usedSymbols,
  } = analyzeTemplateRequirements(template)

  const manifest: RenderManifest = {
    zplSha256,
    pipelineVersion: PIPELINE_VERSION,
    // Non-deterministic by design — see top-of-function note.
    generatedAt: new Date().toISOString(),
    templateId: template.id,
    printerProfileId: printerProfile.id,
    warnings: buildWarnings,
    usedFonts,
    usedImages,
    usedSymbols,
  }

  return {
    zpl,
    manifest,
    usedFonts,
    usedImages,
    usedSymbols,
    warnings: buildWarnings,
  }
}
